import type {
  PresenceEntry,
  PresenceStatus,
  SocialClientMessage,
  SocialServerMessage,
} from "@mmotdg/shared";
import { appState } from "../state/AppState.js";

const BASE_URL = import.meta.env.VITE_AUTH_SERVER_URL ?? "http://localhost:3001";

function buildWsUrl(token: string): string {
  const httpUrl = new URL(BASE_URL);
  httpUrl.protocol = httpUrl.protocol === "https:" ? "wss:" : "ws:";
  httpUrl.pathname = "/ws";
  httpUrl.searchParams.set("token", token);
  return httpUrl.toString();
}

type Listener = (msg: SocialServerMessage) => void;

class SocialSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private presence = new Map<string, PresenceEntry>();
  private connected = false;
  private pendingReconnect: number | null = null;
  private reconnectDelay = 1000;
  private pingHandle: number | null = null;
  private shouldConnect = false;

  connect(): void {
    this.shouldConnect = true;
    const token = appState.get().token;
    if (!token) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.openSocket(token);
  }

  disconnect(): void {
    this.shouldConnect = false;
    this.connected = false;
    if (this.pendingReconnect !== null) {
      clearTimeout(this.pendingReconnect);
      this.pendingReconnect = null;
    }
    if (this.pingHandle !== null) {
      clearInterval(this.pingHandle);
      this.pingHandle = null;
    }
    if (this.ws) {
      const sock = this.ws;
      this.ws = null;
      try {
        sock.close(1000);
      } catch {
        // ignore
      }
    }
    this.presence.clear();
  }

  isConnected(): boolean {
    return this.connected;
  }

  send(msg: SocialClientMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  setStatus(status: PresenceStatus): void {
    this.send({ type: "presence_set", status });
  }

  onMessage(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getPresence(userId: string): PresenceEntry | null {
    return this.presence.get(userId) ?? null;
  }

  allPresence(): PresenceEntry[] {
    return Array.from(this.presence.values());
  }

  private openSocket(token: string): void {
    const ws = new WebSocket(buildWsUrl(token));
    this.ws = ws;
    ws.addEventListener("open", () => {
      this.connected = true;
      this.reconnectDelay = 1000;
      this.startPing();
    });
    ws.addEventListener("close", () => {
      this.connected = false;
      this.stopPing();
      if (this.shouldConnect) this.scheduleReconnect();
    });
    ws.addEventListener("error", () => {
      this.connected = false;
    });
    ws.addEventListener("message", (event) => {
      let msg: SocialServerMessage;
      try {
        msg = JSON.parse(typeof event.data === "string" ? event.data : "") as SocialServerMessage;
      } catch {
        return;
      }
      this.handleMessage(msg);
    });
  }

  private handleMessage(msg: SocialServerMessage): void {
    if (msg.type === "presence_snapshot") {
      this.presence.clear();
      for (const entry of msg.entries) this.presence.set(entry.userId, entry);
    } else if (msg.type === "presence_update") {
      if (msg.entry.status === "offline") {
        this.presence.delete(msg.entry.userId);
      } else {
        this.presence.set(msg.entry.userId, msg.entry);
      }
    }
    for (const l of this.listeners) l(msg);
  }

  private startPing(): void {
    if (this.pingHandle !== null) return;
    this.pingHandle = window.setInterval(() => {
      this.send({ type: "ping" });
    }, 25000);
  }

  private stopPing(): void {
    if (this.pingHandle !== null) {
      clearInterval(this.pingHandle);
      this.pingHandle = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.pendingReconnect !== null) return;
    const delay = this.reconnectDelay;
    this.pendingReconnect = window.setTimeout(() => {
      this.pendingReconnect = null;
      if (!this.shouldConnect) return;
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
      const token = appState.get().token;
      if (token) this.openSocket(token);
    }, delay);
  }
}

export const socialSocket = new SocialSocket();
